import { Card, Text } from '@components/ui';
import { useRatingSpecialMessageQuery, useRatingVideosQuery } from '@lib/api/rating';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';

const VIDEO_ASPECT_RATIO = '16 / 9';

export const WalkAroundVideos = ({ jobId }: { jobId: string }): JSX.Element => {
  const { t } = useTranslation();
  const { data: videos = [], isLoading } = useRatingVideosQuery(jobId);

  return (
    <Box component="section" aria-labelledby="rating-videos-title">
      <Card>
        <Text
          as="h2"
          id="rating-videos-title"
          variant="subtitle"
          sx={{ mb: 2, fontWeight: 700 }}
        >
          {t('ratingDetail.videos')}
        </Text>
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{
              width: '100%',
              height: 'auto',
              aspectRatio: VIDEO_ASPECT_RATIO,
            }}
          />
        ) : videos.length === 0 ? (
          <Text tone="muted" sx={{ fontSize: 14 }}>
            {t('ratingDetail.noVideos')}
          </Text>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                videos.length === 1
                  ? 'minmax(0, 1fr)'
                  : { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            {videos.map((video) => (
              <Box
                key={video.id ?? video.url}
                sx={{
                  aspectRatio: VIDEO_ASPECT_RATIO,
                  overflow: 'hidden',
                  borderRadius: 2,
                  bgcolor: 'common.black',
                }}
              >
                <ReactPlayer
                  url={video.url}
                  width="100%"
                  height="100%"
                  controls
                />
              </Box>
            ))}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export const R8trNotes = ({ jobId }: { jobId: string }): JSX.Element | null => {
  const { t } = useTranslation();
  const { data: note } = useRatingSpecialMessageQuery(jobId);

  if (!note) return null;

  return (
    <Box component="section" aria-labelledby="rating-notes-title">
      <Card>
        <Text
          as="h2"
          id="rating-notes-title"
          variant="subtitle"
          sx={{ mb: 1.5, fontWeight: 700 }}
        >
          {t('ratingDetail.notes')}
        </Text>
        <Text
          sx={{
            fontSize: 15,
            lineHeight: 1.6,
            whiteSpace: 'pre-line',
            color: 'text.secondary',
          }}
        >
          {note}
        </Text>
      </Card>
    </Box>
  );
};
